import { z } from "zod";
import {
  CONSOLIDATION_CURRENCY,
  INTERCOMPANY_MODES,
  MAX_CONSOLIDATED_COMPANIES,
  type FinancePosition,
  type GroupCards,
  type GroupDre,
  type IntercompanyMode,
  type MonthForecast,
} from "@cx/domain";
import { refinePair } from "./dashboard";
import { companyCnpjInput } from "./fiscal";
import { text } from "./fields";
import { ulidParam } from "./ids";
import { businessDate } from "./prices";

// ===== Multiempresa avançada (T3.10): dashboard consolidado e inclusão de empresa =====

/** `companies` = ids separados por vírgula (até 20, sem repetir); ausente = todas as empresas que o usuário vê. */
const companies = z
  .string()
  .max(MAX_CONSOLIDATED_COMPANIES * 27 + 10)
  .transform((v) => v.split(",").filter(Boolean))
  .pipe(
    z
      .array(ulidParam)
      .min(1, "Escolha as empresas")
      .max(MAX_CONSOLIDATED_COMPANIES, `No máximo ${MAX_CONSOLIDATED_COMPANIES} empresas`)
      .refine((l) => new Set(l).size === l.length, "Empresa repetida"),
  )
  .optional();

/** Consolidado (RF-UI-06 + RF-BI-01..03): período e comparação como no Mensal; eliminação intercompanhia ligada. */
export const consolidatedQuery = z
  .object({
    from: businessDate.optional(),
    to: businessDate.optional(),
    compareFrom: businessDate.optional(),
    compareTo: businessDate.optional(),
    companies,
    intercompany: z.enum(INTERCOMPANY_MODES, "Opção inválida").default("eliminate"),
  })
  .superRefine((v, ctx) => {
    refinePair(v, ctx, "from", "to", "do período");
    refinePair(v, ctx, "compareFrom", "compareTo", "da comparação");
  });
export type ConsolidatedQuery = z.output<typeof consolidatedQuery>;

/**
 * O que o usuário pode ver em cada empresa da consolidação. Montado pela API a partir da sessão (empresas liberadas ×
 * papel), nunca da requisição; o DO só lê as empresas da lista e só as colunas concedidas.
 */
export type ConsolidationGrant = {
  companyId: string;
  /** products:read_cost: custo, margem e lucro. */
  cost: boolean;
  receivables: boolean;
  payables: boolean;
  /** financial_accounts:read: saldo de caixa e bancos. */
  accounts: boolean;
};

/** "+ Adicionar empresa" (RF-UI-06): o resto do perfil fiscal segue em `PUT /fiscal/company` da empresa nova. */
export const companyCreateInput = z.object({
  legalName: z
    .string("Informe a razão social")
    .trim()
    .min(1, "Informe a razão social")
    .max(200, "Até 200 caracteres"),
  tradeName: text(60).optional(),
  cnpj: companyCnpjInput.shape.cnpj.optional(),
});
export type CompanyCreateInput = z.output<typeof companyCreateInput>;
export type CompanyCreated = { id: string; legalName: string; tradeName: string | null; cnpj: string | null };

// ---------- resposta ----------

export type IntercompanyFinance = {
  receivableOpenCents?: number | null;
  receivableOverdueCents?: number | null;
  payableOpenCents?: number | null;
  payableOverdueCents?: number | null;
};

export type ConsolidatedCompany = {
  id: string;
  name: string;
  cnpj: string | null;
  /** Situações que contam como venda nesta empresa (configuração dela). */
  statuses: string[];
  /** Vendas do período como no Mensal da empresa (bruto). */
  gross: { totalCents: number | null; orders: number | null; qtyQ4: number | null };
  /** Parte do bruto vendida a outras empresas da consolidação. */
  intercompany: { totalCents: number | null; orders: number | null };
  /** Base do consolidado (sem as vendas intercompanhia quando `intercompany = eliminate`). */
  cards: GroupCards;
  dre: GroupDre;
  sharePct4: number | null;
  /** Valor por dia (base), mesmos `days`. */
  daily: (number | null)[];
  /** Posição de hoje (bruta), só com as colunas concedidas, e a parte com as outras empresas da consolidação. */
  finance: FinancePosition;
  financeIntercompany: IntercompanyFinance;
};

export type ConsolidatedDashboard = {
  currency: typeof CONSOLIDATION_CURRENCY;
  intercompany: IntercompanyMode;
  period: { from: string; to: string };
  compare: { from: string; to: string };
  days: string[];
  compareDays: string[];
  companies: ConsolidatedCompany[];
  total: {
    cards: GroupCards;
    dre: GroupDre;
    daily: (number | null)[];
    compareDaily: (number | null)[];
    /** Posição de hoje do grupo (sem as contas entre as empresas quando elimina). */
    finance: FinancePosition;
  };
  /** Vendas entre empresas da consolidação no período: de quem vendeu para quem comprou. */
  intercompanyPairs: {
    fromCompanyId: string;
    toCompanyId: string;
    totalCents: number | null;
    orders: number | null;
  }[];
  /** Previsão do fechamento do grupo: só quando o período é o mês corrente até hoje. */
  forecast: MonthForecast | null;
};
